import i18n from "../language/i18n";

export const retrieveColorByScore = (score: number) => {
  if (score <= 25) {
    return "#F96666";
  } else if (score > 25 && score <= 50) {
    return "#f98d66";
  } else if (score > 50 && score <= 75) {
    return "#cbd357";
  } else {
    return "#ABD357";
  }
};

export const retrieveContentTypeOptionList = (textOnly: boolean) => {
  if (textOnly) {
    return [
      {
        id: "TEXT",
        value: "TEXT",
        text: i18n.t("TEXT"),
        icon: "text-gray",
      },
    ];
  } else {
    return [
      {
        id: "TEXT",
        value: "TEXT",
        text: i18n.t("TEXT"),
        icon: "text-gray",
      },
      {
        id: "IMAGE",
        value: "IMAGE",
        text: i18n.t("IMAGE"),
        icon: "image-gray",
      },
      {
        id: "VIDEO",
        value: "VIDEO",
        text: i18n.t("VIDEO"),
        icon: "video-gray",
      },
      {
        id: "AUDIO",
        value: "AUDIO",
        text: i18n.t("AUDIO"),
        icon: "audio-gray",
      },
    ];
  }
};

export const retrieveStatusOptionList = () => {
  return [
    {
      value: "ACTIVE",
      text: i18n.t("ACTIVE"),
    },
    {
      value: "PASSIVE",
      text: i18n.t("PASSIVE"),
    },
  ];
};

export const retrieveDifficultyOptionList = () => {
  return [
    {
      value: "EASY",
      text: i18n.t("EASY"),
    },
    {
      value: "MEDIUM",
      text: i18n.t("MEDIUM"),
    },
    {
      value: "HARD",
      text: i18n.t("HARD"),
    },
  ];
};

export const retrieveDurationTypeOptionList = () => {
  return [
    { value: "NONE", text: i18n.t("NONE") },
    { value: "ALL", text: i18n.t("ALL") },
    { value: "EACH", text: i18n.t("EACH") },
  ];
};

export const retrieveDurationInMinutesOptionList = (durationType: string) => {
  if (durationType === "ALL") {
    return [
      { value: 15, text: 15 },
      { value: 30, text: 30 },
      { value: 45, text: 45 },
      { value: 60, text: 60 },
      { value: 75, text: 75 },
      { value: 90, text: 90 },
      { value: 105, text: 105 },
      { value: 120, text: 120 },
    ];
  } else if (durationType === "EACH") {
    return [
      { value: 1, text: 1 },
      { value: 2, text: 2 },
      { value: 3, text: 3 },
      { value: 5, text: 5 },
      { value: 10, text: 10 },
    ];
  }
  return [];
};

export const retrieveDurationInSecondsOptionList = () => {
  return [
    { value: 15, text: `15 ${i18n.t("seconds")}` },
    { value: 20, text: `20 ${i18n.t("seconds")}` },
    { value: 25, text: `25 ${i18n.t("seconds")}` },
    { value: 30, text: `30 ${i18n.t("seconds")}` },
    { value: 35, text: `35 ${i18n.t("seconds")}` },
    { value: 40, text: `40 ${i18n.t("seconds")}` },
    { value: 45, text: `45 ${i18n.t("seconds")}` },
  ];
};

// Sentinel standing in for an already-correct "$$" while single dollars are
// paired, so a `$a$$b$` run cannot be mis-paired across it. U+0000 never occurs
// in authored or generated content; if it somehow did we skip the pass rather
// than corrupt the text.
const MATH_SENTINEL = "\u0000";

// Inline math written with a single dollar. Strict pairing, mirroring the
// backend's ParseOperations.INLINE_DOLLAR_MATH: the opening "$" must be followed
// by a non-space, the closing "$" preceded by a non-space, and the span may not
// cross a newline or swallow another dollar. A loose /\$(.+?)\$/ pairs a currency
// amount with the next stray dollar ("5 $ … 3 $") and renders the prose between
// them as math.
const INLINE_DOLLAR_MATH =
  /\$([^\n$\s\u0000](?:[^\n$\u0000]*[^\n$\s\u0000])?)\$/g;
const BRACKET_DISPLAY_MATH = /\\\[([\s\S]+?)\\\]/g;
const BRACKET_INLINE_MATH = /\\\(([\s\S]+?)\\\)/g;

// Rewrites $…$ / \[…\] / \(…\) into the $$…$$ this file's tokenizer splits on.
//
// ai-service asks every model for $$…$$ and normalizes what it can (see
// ParseOperations.normalizeLatexDelimiters), but rows generated before that
// landed are already stored with single dollars — and a single "$" carries zero
// "$$" markers, so the formula reaches the learner as literal text. Normalizing
// on read repairs that existing content without a data migration, and keeps the
// three renderers (admin-web, app-web, mobile) on one delimiter contract.
//
// Every rewrite inserts a balanced pair, so this can never hand the splitter an
// odd "$$" count — which would flip the trailing prose into an equation.
export const normalizeLatexDelimiters = (inputText: string) => {
  if (!inputText) return inputText;
  let normalized = inputText
    .replace(BRACKET_DISPLAY_MATH, (_match, body) => `$$${body}$$`)
    .replace(BRACKET_INLINE_MATH, (_match, body) => `$$${body}$$`);
  if (!normalized.includes("$") || normalized.includes(MATH_SENTINEL)) {
    return normalized;
  }
  normalized = normalized.split("$$").join(MATH_SENTINEL);
  normalized = normalized.replace(
    INLINE_DOLLAR_MATH,
    (_match, body) => `${MATH_SENTINEL}${body}${MATH_SENTINEL}`,
  );
  return normalized.split(MATH_SENTINEL).join("$$");
};

export const constructInputWithSpecialExpressionList = (inputText: string) => {
  const inputPartList = [];
  if (!inputText) return [];

  const splittedBold = normalizeLatexDelimiters(inputText).split("**");
  let isBold = false;

  for (let i = 0; i < splittedBold.length; i++) {
    let isUnderline = false;
    const splittedUnderline = splittedBold[i].split("__");

    for (let j = 0; j < splittedUnderline.length; j++) {
      let isEquation = false;
      const splittedEquation = splittedUnderline[j].split("$$");

      for (let k = 0; k < splittedEquation.length; k++) {
        let isBacktickEquation = false;
        const splittedBacktick = splittedEquation[k].split("`");

        for (let l = 0; l < splittedBacktick.length; l++) {
          inputPartList.push({
            value: splittedBacktick[l],
            isEquation: isEquation || isBacktickEquation,
            isUnderline,
            isBold,
          });
          isBacktickEquation = !isBacktickEquation;
        }
        isEquation = !isEquation;
      }
      isUnderline = !isUnderline;
    }
    isBold = !isBold;
  }

  return inputPartList;
};

export const retrieveStandardExamTypeOptionList = () => {
  return [
    { value: "AYT", text: "AYT" },
    { value: "TYT", text: "TYT" },
    { value: "LGS", text: "LGS" },
  ];
};

export const retrieveStandardExamTypeIcon = (baseReportType: string) => {
  if (baseReportType === "TYT") {
    return "tyt";
  } else if (baseReportType === "AYT") {
    return "ayt";
  } else if (baseReportType === "LGS") {
    return "lgs";
  }
};

export const retrieveCoterieTypeFromStandardExamCoterieType = (
  type: string,
  coterieType: string
) => {
  if (coterieType === "TURKISH") {
    return "TURKISH";
  } else if (coterieType === "SCIENCE") {
    return "SCIENCE";
  } else if ((type === "TYT" || type === "AYT") && coterieType === "PHYSICS") {
    return "PHYSICS";
  } else if (type === "LGS" && coterieType === "PHYSICS") {
    return "SOCIAL_STUDIES";
  } else if (
    (type === "TYT" || type === "AYT") &&
    coterieType === "CHEMISTRY"
  ) {
    return "CHEMISTRY";
  } else if (type === "LGS" && coterieType === "CHEMISTRY") {
    return "SOCIAL_STUDIES";
  } else if ((type === "TYT" || type === "AYT") && coterieType === "BIOLOGY") {
    return "BIOLOGY";
  } else if (type === "LGS" && coterieType === "BIOLOGY") {
    return "SOCIAL_STUDIES";
  } else if ((type === "TYT" || type === "AYT") && coterieType === "HISTORY") {
    return "HISTORY";
  } else if (
    (type === "TYT" || type === "AYT") &&
    coterieType === "GEOGRAPHY"
  ) {
    return "GEOGRAPHY";
  } else if (
    (type === "LGS" && coterieType === "HISTORY") ||
    (type === "LGS" && coterieType === "GEOGRAPHY") ||
    coterieType === "REVOLUTION_HISTORY"
  ) {
    return "SOCIAL_STUDIES";
  } else if (coterieType === "HISTORY_I" || coterieType === "HISTORY_II") {
    return "HISTORY";
  } else if (coterieType === "GEOGRAPHY_I" || coterieType === "GEOGRAPHY_II") {
    return "GEOGRAPHY";
  } else if (coterieType === "MATHEMATICS" || coterieType === "GEOMETRY") {
    return "MATHEMATICS";
  } else if (
    coterieType === "PHILOSOPHY" ||
    coterieType === "PHILOSOPHY_OR_CULTURE_AND_RELIGION_KNOWLEDGE"
  ) {
    return "PHILOSOPHY";
  } else if (
    (type === "TYT" || type === "AYT") &&
    coterieType === "CULTURE_AND_RELIGION_KNOWLEDGE"
  ) {
    return "CULTURE_AND_RELIGION_KNOWLEDGE";
  } else if (
    type === "LGS" &&
    coterieType === "CULTURE_AND_RELIGION_KNOWLEDGE"
  ) {
    return "CULTURE_AND_RELIGION_KNOWLEDGE";
  } else if (coterieType === "ENGLISH") {
    return "ENGLISH";
  } else if (coterieType === "LITERATURE") {
    return "LITERATURE";
  }
};

export const retrieveStandardExamCoterieTypeOptionListByStandardExamType = (
  type: string
) => {
  if (type === "TYT") {
    return retrieveStandardExamCoterieTypeOptionList().filter(
      (item) =>
        item.value === "TURKISH" ||
        item.value === "HISTORY" ||
        item.value === "GEOGRAPHY" ||
        item.value === "PHILOSOPHY" ||
        item.value === "CULTURE_AND_RELIGION_KNOWLEDGE" ||
        item.value === "MATHEMATICS" ||
        item.value === "GEOMETRY" ||
        item.value === "PHYSICS" ||
        item.value === "CHEMISTRY" ||
        item.value === "BIOLOGY"
    );
  } else if (type === "AYT") {
    return retrieveStandardExamCoterieTypeOptionList().filter(
      (item) =>
        item.value === "LITERATURE" ||
        item.value === "HISTORY_I" ||
        item.value === "HISTORY_II" ||
        item.value === "GEOGRAPHY_I" ||
        item.value === "GEOGRAPHY_II" ||
        item.value === "PHILOSOPHY" ||
        item.value === "PHILOSOPHY_OR_CULTURE_AND_RELIGION_KNOWLEDGE" ||
        item.value === "MATHEMATICS" ||
        item.value === "GEOMETRY" ||
        item.value === "PHYSICS" ||
        item.value === "CHEMISTRY" ||
        item.value === "BIOLOGY" ||
        item.value === "ENGLISH"
    );
  } else if (type === "LGS") {
    return retrieveStandardExamCoterieTypeOptionList().filter(
      (item) =>
        item.value === "TURKISH" ||
        item.value === "REVOLUTION_HISTORY" ||
        item.value === "CULTURE_AND_RELIGION_KNOWLEDGE" ||
        item.value === "MATHEMATICS" ||
        item.value === "SCIENCE" ||
        item.value === "ENGLISH"
    );
  } else return [];
};

const retrieveStandardExamCoterieTypeOptionList = () => {
  return [
    { value: "TURKISH", text: i18n.t("TURKISH") },
    { value: "SCIENCE", text: i18n.t("SCIENCE") },
    { value: "HISTORY", text: i18n.t("HISTORY") },
    { value: "GEOGRAPHY", text: i18n.t("GEOGRAPHY") },
    { value: "PHILOSOPHY", text: i18n.t("PHILOSOPHY") },
    {
      value: "CULTURE_AND_RELIGION_KNOWLEDGE",
      text: i18n.t("CULTURE_AND_RELIGION_KNOWLEDGE"),
    },
    { value: "MATHEMATICS", text: i18n.t("MATHEMATICS") },
    { value: "GEOMETRY", text: i18n.t("GEOMETRY") },
    { value: "PHYSICS", text: i18n.t("PHYSICS") },
    { value: "CHEMISTRY", text: i18n.t("CHEMISTRY") },
    { value: "BIOLOGY", text: i18n.t("BIOLOGY") },
    { value: "LITERATURE", text: i18n.t("LITERATURE") },
    { value: "HISTORY_I", text: i18n.t("HISTORY_I") },
    { value: "HISTORY_II", text: i18n.t("HISTORY_II") },
    { value: "GEOGRAPHY_I", text: i18n.t("GEOGRAPHY_I") },
    { value: "GEOGRAPHY_II", text: i18n.t("GEOGRAPHY_II") },
    {
      value: "PHILOSOPHY_OR_CULTURE_AND_RELIGION_KNOWLEDGE",
      text: i18n.t("PHILOSOPHY_OR_CULTURE_AND_RELIGION_KNOWLEDGE"),
    },
    { value: "ENGLISH", text: i18n.t("ENGLISH") },
    { value: "REVOLUTION_HISTORY", text: i18n.t("REVOLUTION_HISTORY") },
  ];
};

const retrieveValidationRequirementList_AYT = () => {
  return [
    {
      value: "LITERATURE",
      count: 24,
    },
    {
      value: "HISTORY_I",
      count: 10,
    },
    {
      value: "HISTORY_II",
      count: 11,
    },
    {
      value: "GEOGRAPHY_I",
      count: 6,
    },
    {
      value: "GEOGRAPHY_II",
      count: 11,
    },
    {
      value: "PHILOSOPHY",
      count: 12,
    },
    {
      value: "PHILOSOPHY_OR_CULTURE_AND_RELIGION_KNOWLEDGE",
      count: 6,
    },
    {
      value: "MATHEMATICS",
      count: 30,
    },
    {
      value: "GEOMETRY",
      count: 10,
    },
    {
      value: "PHYSICS",
      count: 14,
    },
    {
      value: "CHEMISTRY",
      count: 13,
    },
    {
      value: "BIOLOGY",
      count: 13,
    },
    {
      value: "ENGLISH",
      count: 80,
    },
  ];
};

const retrieveValidationRequirementList_TYT = () => {
  return [
    {
      value: "TURKISH",
      count: 40,
    },
    {
      value: "HISTORY",
      count: 5,
    },
    {
      value: "GEOGRAPHY",
      count: 5,
    },
    {
      value: "PHILOSOPHY",
      count: 5,
    },
    {
      value: "CULTURE_AND_RELIGION_KNOWLEDGE",
      count: 5,
    },
    {
      value: "MATHEMATICS",
      count: 30,
    },
    {
      value: "GEOMETRY",
      count: 10,
    },
    {
      value: "PHYSICS",
      count: 7,
    },
    {
      value: "CHEMISTRY",
      count: 7,
    },
    {
      value: "BIOLOGY",
      count: 6,
    },
  ];
};

const retrieveValidationRequirementList_LGS = () => {
  return [
    {
      value: "TURKISH",
      count: 20,
    },
    {
      value: "REVOLUTION_HISTORY",
      count: 10,
    },
    {
      value: "CULTURE_AND_RELIGION_KNOWLEDGE",
      count: 10,
    },
    {
      value: "MATHEMATICS",
      count: 20,
    },
    {
      value: "SCIENCE",
      count: 20,
    },
    {
      value: "ENGLISH",
      count: 10,
    },
  ];
};

export const retrieveValidationRequirementList = (type: string) => {
  if (type === "TYT") {
    return retrieveValidationRequirementList_TYT();
  } else if (type === "AYT") {
    return retrieveValidationRequirementList_AYT();
  } else if (type === "LGS") {
    return retrieveValidationRequirementList_LGS();
  }
  return [];
};

export const constructActivityItemListWithSolutionForAI = (
  bodyMap: any,
  materialMap: any,
  type: string,
  imageContentList: any
) => {
  const itemList = [];
  Object.keys(bodyMap).forEach((key) => {
    const bodyItem = bodyMap[key];
    if (bodyItem.type === "IMAGE") {
      if (imageContentList && imageContentList.length > 0) {
        const foundImageContent = imageContentList.find(
          (imageContent: any) => imageContent.url === bodyItem.value
        );
        itemList.push({
          type: "text",
          text: foundImageContent.content,
        });
      } else {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      }
    } else {
      itemList.push({
        type: "text",
        text: bodyItem.value.replaceAll("@@", "___________"),
      });
    }
  });
  if (type === "ORDERING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_order")}: `,
      });
      Object.keys(materialMap).forEach((key) => {
        itemList.push({
          type: "text",
          text: materialMap[key],
        });
      });
    }
  } else if (type === "DROPDOWN") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        itemList.push({
          type: "text",
          text: `${i18n.t("blank")} ${index + 1}: ${
            Object.keys(materialMap[key])[0]
          }`,
        });
      });
    }
  } else if (type === "MCSA") {
    if (Object.keys(materialMap).length > 0) {
      const answer = Object.keys(materialMap)[0];
      materialMap[answer].forEach((option: string) => {
        itemList.push({
          type: "text",
          text: option,
        });
      });
      materialMap[answer].forEach((option: string) => {
        if (option === answer) {
          itemList.push({
            type: "text",
            text: `[${i18n.t("answer").toUpperCase()}]${option}`,
          });
        }
      });
    }
  } else if (type === "MCMA") {
    if (Object.keys(materialMap).length > 0) {
      const answer = Object.keys(materialMap)[0];
      materialMap[answer].forEach((option: string) => {
        itemList.push({
          type: "text",
          text: option,
        });
      });
      const splittedTextList = answer.split("§");
      materialMap[answer].forEach((option: string) => {
        if (splittedTextList.includes(option)) {
          itemList.push({
            type: "text",
            text: `[${i18n.t("answer").toUpperCase()}]${option}`,
          });
        }
      });
    }
  } else if (type === "MATCHING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_matching")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        const matchingKey = Object.keys(materialMap[key])[0];
        const matchingValue = materialMap[key][matchingKey];
        itemList.push({
          type: "text",
          text: `${matchingKey}: ${matchingValue}`,
        });
      });
    }
  } else if (type === "GROUPING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_grouping")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        let text = `${i18n.t("group")} ${index + 1}: `;
        itemList.push({
          type: "text",
          text: text,
        });

        const groupName = Object.keys(materialMap[key])[0];
        let currentText = "";
        materialMap[key][groupName].forEach((item: string) => {
          currentText += `${item}, `;
        });
        currentText = currentText.substring(0, currentText.length - 2);
        itemList.push({
          type: "text",
          text: `${groupName}: ${currentText}`,
        });
      });
    }
  } else if (type === "FILL_IN_THE_BLANKS") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        materialMap[key].forEach((item: string) => {
          itemList.push({
            type: "text",
            text: `${i18n.t("blank")} ${index + 1}: ${item}`,
          });
        });
      });
    }
  } else if (type === "OPEN_ENDED") {
  } else if (type === "TRUE_FALSE") {
    if (materialMap.trueList && Object.keys(materialMap.trueList).length > 0) {
      materialMap.trueList.forEach((item: string) => {
        itemList.push({
          type: "text",
          text: `[${i18n.t("correct").toUpperCase()}]${item}`,
        });
      });
    }

    if (
      materialMap.falseList &&
      Object.keys(materialMap.falseList).length > 0
    ) {
      materialMap.falseList.forEach((item: string) => {
        itemList.push({
          type: "text",
          text: `[${i18n.t("incorrect").toUpperCase()}]${item}`,
        });
      });
    }
  }

  return itemList;
};

export const constructActivityItemListWithAnswersForAI = (
  bodyMap: any,
  materialMap: any,
  type: string
) => {
  const itemList = [];
  if (type === "ORDERING") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_order")}: `,
      });
      Object.keys(materialMap).forEach((key) => {
        itemList.push({
          type: "text",
          text: materialMap[key],
        });
      });
    }
  } else if (type === "DROPDOWN") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value.replaceAll("@@", "___________"),
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        itemList.push({
          type: "text",
          text: `${index + 1}. ${Object.keys(materialMap[key])[0]}`,
        });
      });
    }
  } else if (type === "MCSA") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answer")}: `,
      });
      Object.keys(materialMap).forEach((key) => {
        itemList.push({
          type: "text",
          text: key,
        });
      });
    }
  } else if (type === "MCMA") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      const splittedTextList = Object.keys(materialMap)[0].split("§");
      splittedTextList.forEach((item) => {
        const parsedItemList = JSON.parse(item);
        for (const parsedItem of parsedItemList) {
          itemList.push({
            type: "text",
            text: parsedItem,
          });
        }
      });
    }
  } else if (type === "MATCHING") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        itemList.push({
          type: "text",
          text: `${i18n.t("match")} ${index + 1}:`,
        });
        itemList.push({
          type: "text",
          text: `${key}: ${materialMap[key]}`,
        });
      });
    }
  } else if (type === "GROUPING") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        let text = `${i18n.t("group")} ${index + 1}: `;
        itemList.push({
          type: "text",
          text: text,
        });

        const groupName = key;
        let currentText = "";
        materialMap[groupName].forEach((item: string) => {
          currentText += `${item}, `;
        });
        currentText = currentText.substring(0, currentText.length - 2);
        itemList.push({
          type: "text",
          text: `${groupName}: [${currentText}]`,
        });
      });
    }
  } else if (type === "FILL_IN_THE_BLANKS") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value.replaceAll("@@", "___________"),
        });
      }
    });
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        JSON.parse(materialMap[key]).forEach((item: string) => {
          itemList.push({
            type: "text",
            text: `${index + 1}. ${item}`,
          });
        });
      });
    }
  } else if (type === "OPEN_ENDED") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
  } else if (type === "TRUE_FALSE") {
    Object.keys(bodyMap).forEach((key) => {
      const bodyItem = JSON.parse(bodyMap[key]);
      if (bodyItem.type === "IMAGE") {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      } else {
        itemList.push({
          type: "text",
          text: bodyItem.value,
        });
      }
    });
    if (materialMap.trueList && Object.keys(materialMap.trueList).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      materialMap.trueList.forEach((item: string) => {
        itemList.push({
          type: "text",
          text: item,
        });
      });
    }
    if (
      materialMap.falseList &&
      Object.keys(materialMap.falseList).length > 0
    ) {
      itemList.push({
        type: "text",
        text: `${i18n.t("incorrect_answers")}: `,
      });
      materialMap.falseList.forEach((item: string) => {
        itemList.push({
          type: "text",
          text: item,
        });
      });
    }
  }

  return itemList;
};

export const constructActivityItemListForSolution = (
  bodyMap: any,
  materialMap: any,
  type: string,
  imageContentList: any[]
) => {
  const itemList = [];
  Object.keys(bodyMap).forEach((key) => {
    const bodyItem = bodyMap[key];
    if (bodyItem.type === "IMAGE") {
      if (imageContentList && imageContentList.length > 0) {
        const foundImageContent = imageContentList.find(
          (imageContent) => imageContent.url === bodyItem.value
        );
        itemList.push({
          type: "text",
          text: foundImageContent.content,
        });
      } else {
        itemList.push({
          type: "image_url",
          imageUrl: bodyItem.value,
        });
      }
    } else {
      itemList.push({
        type: "text",
        text: bodyItem.value.replaceAll("@@", "___________"),
      });
    }
  });
  if (type === "ORDERING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_order")}: `,
      });
      Object.keys(materialMap).forEach((key) => {
        itemList.push({
          type: "text",
          text: materialMap[key],
        });
      });
    }
  } else if (type === "DROPDOWN") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        itemList.push({
          type: "text",
          text: `${i18n.t("blank")} ${index + 1}: ${
            Object.keys(materialMap[key])[0]
          }`,
        });
      });
    }
  } else if (type === "MCSA") {
    if (Object.keys(materialMap).length > 0) {
      const answer = Object.keys(materialMap)[0];
      materialMap[answer].forEach((option: string) => {
        itemList.push({
          type: "text",
          text: option,
        });
      });
      materialMap[answer].forEach((option: string) => {
        if (option === answer) {
          itemList.push({
            type: "text",
            text: `[${i18n.t("answer").toUpperCase()}]${option}`,
          });
        }
      });
    }
  } else if (type === "MCMA") {
    if (Object.keys(materialMap).length > 0) {
      const answer = Object.keys(materialMap)[0];
      materialMap[answer].forEach((option: string) => {
        itemList.push({
          type: "text",
          text: option,
        });
      });
      const splittedTextList = answer.split("§");
      materialMap[answer].forEach((option: string) => {
        if (splittedTextList.includes(option)) {
          itemList.push({
            type: "text",
            text: `[${i18n.t("answer").toUpperCase()}]${option}`,
          });
        }
      });
    }
  } else if (type === "MATCHING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_matching")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        const matchingKey = Object.keys(materialMap[key])[0];
        const matchingValue = materialMap[key][matchingKey];
        itemList.push({
          type: "text",
          text: `${matchingKey}: ${matchingValue}`,
        });
      });
    }
  } else if (type === "GROUPING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_grouping")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        let text = `${i18n.t("group")} ${index + 1}: `;
        itemList.push({
          type: "text",
          text: text,
        });

        const groupName = Object.keys(materialMap[key])[0];
        let currentText = "";
        materialMap[key][groupName].forEach((item: string) => {
          currentText += `${item}, `;
        });
        currentText = currentText.substring(0, currentText.length - 2);
        itemList.push({
          type: "text",
          text: `${groupName}: ${currentText}`,
        });
      });
    }
  } else if (type === "FILL_IN_THE_BLANKS") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        materialMap[key].forEach((item: string) => {
          itemList.push({
            type: "text",
            text: `${i18n.t("blank")} ${index + 1}: ${item}`,
          });
        });
      });
    }
  } else if (type === "OPEN_ENDED") {
  } else if (type === "TRUE_FALSE") {
    if (materialMap.trueList && Object.keys(materialMap.trueList).length > 0) {
      materialMap.trueList.forEach((item: string) => {
        itemList.push({
          type: "text",
          text: `[${i18n.t("correct").toUpperCase()}]${item}`,
        });
      });
    }

    if (
      materialMap.falseList &&
      Object.keys(materialMap.falseList).length > 0
    ) {
      materialMap.falseList.forEach((item: string) => {
        itemList.push({
          type: "text",
          text: `[${i18n.t("incorrect").toUpperCase()}]${item}`,
        });
      });
    }
  }

  return itemList;
};

export const retrieveActivityTemplateDTOOptionList = (
  activityTemplateSet: any
) => {
  return activityTemplateSet.map((activityTemplateDTO: any) => ({
    id: activityTemplateDTO.id,
    value: activityTemplateDTO.id,
    text: i18n.t(activityTemplateDTO.type),
  }));
};

const retrieveDefaultOrderingDataMap = (orderingMaterialMap: any) => {
  const dataMap: any = {};
  const originalKeys = Object.keys(orderingMaterialMap);
  const shuffledKeys = [...originalKeys];

  for (let i = shuffledKeys.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledKeys[i], shuffledKeys[j]] = [shuffledKeys[j], shuffledKeys[i]];
  }

  const isSameOrder = shuffledKeys.every(
    (key, index) => key === originalKeys[index]
  );

  if (isSameOrder && shuffledKeys.length >= 2) {
    [shuffledKeys[0], shuffledKeys[1]] = [shuffledKeys[1], shuffledKeys[0]];
  }

  shuffledKeys.forEach((key, index) => {
    dataMap[index] = key;
  });

  return dataMap;
};

const retrieveDefaultDropdownMap = (dropdownMaterialMap: any) => {
  const dataMap: any = {};
  Object.keys(dropdownMaterialMap).forEach((key) => {
    dataMap[key] = "DEFAULT_OPTION";
  });
  return dataMap;
};

const retrieveDefaultMCSAMap = (MCSAMaterialMap: any) => {
  const dataMap: any = {};
  Object.keys(MCSAMaterialMap).forEach((key, index) => {
    dataMap[key] = "ANSWER_KEY";
  });
  return dataMap;
};

const retrieveDefaultMCMAMap = (MCMAMaterialMap: any) => {
  const dataMap: any = {};
  Object.keys(MCMAMaterialMap).forEach((key, index) => {
    dataMap[key] = [];
  });
  return dataMap;
};

const retrieveDefaultMatchingMap = (matchingMaterialMap: any) => {
  const dataMap: any = {};
  Object.keys(matchingMaterialMap).forEach((key, index) => {
    dataMap[key] = "";
  });
  return dataMap;
};

const retrieveDefaultGroupingMap = (groupingMaterialMap: any) => {
  const dataMap: any = {};
  Object.keys(groupingMaterialMap).forEach((key, index) => {
    dataMap[key] = [];
  });
  return dataMap;
};

const retrieveDefaultFillInTheBlanksMap = (fillInTheBlanksMaterialMap: any) => {
  const dataMap: any = {};
  Object.keys(fillInTheBlanksMaterialMap).forEach((key) => {
    dataMap[key] = "";
  });
  return dataMap;
};

const retrieveDefaultOpenEndedMap = () => {
  return { ANSWER: "" };
};

const retrieveDefaultTrueFalseMap = () => {
  return { trueList: [], falseList: [] };
};

export const retrieveCurrentDefaultDataMap = (
  activityTemplateType: any,
  activityData: any
) => {
  const rawMaterial = readActivityDataField(activityData, activityTemplateType, "materialMap");
  if (activityTemplateType === "ORDERING") {
    return retrieveDefaultOrderingDataMap(rawMaterial);
  } else if (activityTemplateType === "DROPDOWN") {
    return retrieveDefaultDropdownMap(rawMaterial);
  } else if (activityTemplateType === "MCSA") {
    return retrieveDefaultMCSAMap(rawMaterial);
  } else if (activityTemplateType === "MCMA") {
    return retrieveDefaultMCMAMap(rawMaterial);
  } else if (activityTemplateType === "MATCHING") {
    return retrieveDefaultMatchingMap(rawMaterial);
  } else if (activityTemplateType === "GROUPING") {
    return retrieveDefaultGroupingMap(rawMaterial);
  } else if (activityTemplateType === "FILL_IN_THE_BLANKS") {
    return retrieveDefaultFillInTheBlanksMap(rawMaterial);
  } else if (activityTemplateType === "OPEN_ENDED") {
    return retrieveDefaultOpenEndedMap();
  } else if (activityTemplateType === "TRUE_FALSE") {
    return retrieveDefaultTrueFalseMap();
  }
  return {};
};

export const constructActivityAnswerMap = (
  activityTemplateType: string,
  activityData: any
) => {
  const rawMaterial =
    activityData != null
      ? activityData.materialMap != null
        ? activityData.materialMap
        : activityData[`${ACTIVITY_DATA_PREFIX[activityTemplateType] || ""}MaterialMap`]
      : undefined;
  if (activityTemplateType === "ORDERING") {
    return retrieveDefaultOrderingDataMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "DROPDOWN") {
    return retrieveDefaultDropdownMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "MCSA") {
    return retrieveDefaultMCSAMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "MCMA") {
    return retrieveDefaultMCMAMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "MATCHING") {
    return retrieveDefaultMatchingMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "GROUPING") {
    return retrieveDefaultGroupingMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "FILL_IN_THE_BLANKS") {
    return retrieveDefaultFillInTheBlanksMap(JSON.parse(rawMaterial));
  } else if (activityTemplateType === "OPEN_ENDED") {
    return retrieveDefaultOpenEndedMap();
  } else if (activityTemplateType === "TRUE_FALSE") {
    return retrieveDefaultTrueFalseMap();
  }
  return {};
};

export const ignoreMathematicalExpression = (inputText: string) => {
  return inputText
    .replaceAll("{", "")
    .replaceAll("}", "")
    .replaceAll("_", "")
    .replaceAll("^", "")
    .replaceAll("\\frac", "")
    .replaceAll("\\text", "")
    .replaceAll("\\sqrt", "");
};

export const constructActivityAnswerStateList = (
  answerList: any[],
  activityList: any[]
) => {
  const stateList: any = [];
  activityList.forEach((activity: any, index: number) => {
    stateList.push({
      index,
      id: activity.id,
      state: checkActivityAnswerState(answerList, activity),
    });
  });
  return stateList;
};

export const retrieveActivityAnswerFromAnswerList = (
  answerList: any[],
  activity: any
) => {
  return answerList.find(
    (answer: any) => parseFloat(answer.activityId) === parseFloat(activity.id)
  );
};

// Verdict computed from the ANSWER ALONE — no activity content required.
//
// For three template types the platform stores the correct answer AS THE ANSWER
// MAP'S KEY: MCSA → the correct option text, MCMA → a JSON array of the correct
// option texts, ORDERING → the correct position. Those answers are therefore
// self-contained, and this is the very comparison the backend's authoritative
// scorers make (catchtivity-service CatchtivityUtilization#calculateMCSAScore /
// #calculateMCMAScore / #calculateOrderingScore). Two things follow, and both are
// the point of grading this way:
//   • no activity `data` is needed, so a content-less activity DTO still grades;
//   • the key is the one captured WHEN THE LEARNER ANSWERED, so the verdict can
//     never disagree with the score the report will show — not even after the
//     item's correct answer is edited underneath an existing answer.
// Returns null for the template types that genuinely need the activity's
// materialMap to be graded (DROPDOWN, MATCHING, GROUPING, FILL_IN_THE_BLANKS,
// TRUE_FALSE) — "I cannot tell", never "wrong".
export const checkIsActivityAnswerCorrectFromAnswerMap = (
  activityTemplateType: string,
  answerMap: any
): boolean | null => {
  if (!answerMap || typeof answerMap !== "object" || Array.isArray(answerMap)) {
    return null;
  }
  const keyList = Object.keys(answerMap);
  // Nothing to grade is not a wrong answer.
  if (keyList.length === 0) return null;

  if (activityTemplateType === "MCSA") {
    return keyList.every(
      (key) => checkAnswerState(key, answerMap[key]) === "CORRECT"
    );
  }

  if (activityTemplateType === "MCMA") {
    return keyList.every((key) => {
      const correctAnswerList = JSON.parse(key);
      const learnerAnswerList = answerMap[key];
      if (!Array.isArray(correctAnswerList)) return false;
      if (!Array.isArray(learnerAnswerList)) return false;
      if (correctAnswerList.length !== learnerAnswerList.length) return false;
      return learnerAnswerList.every(
        (learnerAnswer: string) =>
          checkAnswerState(correctAnswerList, learnerAnswer) === "CORRECT"
      );
    });
  }

  if (activityTemplateType === "ORDERING") {
    // The answer map is { position: materialKeyPlacedThere } (see
    // retrieveDefaultOrderingDataMap), so a fully correct ordering has
    // key === value on every entry — exactly the backend's comparison.
    return keyList.every(
      (key) => checkAnswerState(key + "", answerMap[key] + "") === "CORRECT"
    );
  }

  return null;
};

// Per-activity correctness for the solver nav (step dots + jump panel), aligned
// 1:1 with the activityList. Each entry is:
//   true  → answered AND fully correct       (green ✓)
//   false → answered but wrong               (red ✗)
//   null  → not answered, OR neutral         (no correctness verdict)
// OPEN_ENDED is deliberately neutral (null): it is not auto-graded (awaits
// AI/teacher review) so it must never be painted "wrong". A verdict is only
// produced for a saved, non-empty answer — matching the inline solution reveal
// (serverIndex !== -1). Callers still pass the plain answered-mask separately;
// this only upgrades an "answered" dot to correct/incorrect where known.
//
// The activityList the solve screens pass is the assignment's `activityDTOList`,
// i.e. plain ActivityDTOs, which carry NO `data` (only ActivityWithDataDTO does —
// the solver re-fetches that per question). So the material-map path must treat a
// content-less activity as "no verdict": grading it as `false` painted a red ✗ on
// EVERY answered question regardless of correctness, which is what a learner
// reported as "I answered correctly and it marked me wrong".
export const constructActivityAnswerCorrectnessList = (
  answerList: any[],
  activityList: any[]
): (boolean | null)[] => {
  return activityList.map((activity: any) => {
    const activityAnswer = retrieveActivityAnswerFromAnswerList(
      answerList,
      activity
    );
    if (!activityAnswer || !activityAnswer.data || activityAnswer.isEmpty) {
      return null;
    }
    const { activityTemplateType, data } = activityAnswer;
    if (!activityTemplateType || activityTemplateType === "OPEN_ENDED") {
      return null;
    }
    try {
      const answerOnlyVerdict = checkIsActivityAnswerCorrectFromAnswerMap(
        activityTemplateType,
        data
      );
      if (answerOnlyVerdict !== null) return answerOnlyVerdict;
      // Material-dependent type: only grade it when the activity's content is
      // actually here. No content → no verdict.
      if (
        parseMaterialMapFromData(activity?.data, activityTemplateType) == null
      ) {
        return null;
      }
      return checkIsActivityAnswerFullyCorrect(
        activityTemplateType,
        data,
        activity.data
      );
    } catch (e) {
      // Malformed answer/material JSON must neither paint a verdict nor throw out
      // of the render pass that builds the nav.
      return null;
    }
  });
};

export const checkActivityAnswerState = (answerList: any[], activity: any) => {
  const activityAnswer = retrieveActivityAnswerFromAnswerList(
    answerList,
    activity
  );
  if (!activityAnswer) return "NOT_EXISTS";
  const { data } = activityAnswer;
  if (!data) return "NOT_EXISTS";
  if (activityAnswer.isEmpty) return "EMPTY";
  return "ANSWERED";
};

export const findBestFitActivity = (
  activity: any,
  individualModelList: any,
  outcomeModelList: any
) => {
  const { activityTemplateDTO, data, coterieType, categoryIdList } = activity;
  const activityTemplateDTOList = activityTemplateDTO
    ? [activityTemplateDTO]
    : [];
  let coterieField = "VERBAL";
  if (
    coterieType === "MATHEMATICS" ||
    coterieType === "BIOLOGY" ||
    coterieType === "PHYSICS" ||
    coterieType === "CHEMISTRY" ||
    coterieType === "SCIENCE"
  ) {
    coterieField = "NUMERIC";
  }
  let foundModel;
  let isOutcomeFound = true;
  if (categoryIdList.length > 0) {
    const categoryId = categoryIdList[0];
    foundModel = outcomeModelList.find(
      (outcomeModel: any) => outcomeModel.outcomeId === categoryId
    );
    if (!foundModel) isOutcomeFound = false;
  } else {
    isOutcomeFound = false;
  }
  if (!isOutcomeFound) {
    foundModel = individualModelList.find(
      (individualModel: any) => individualModel.coterieField === coterieField
    );
  }

  let bestScore = Number.MAX_SAFE_INTEGER;
  let bestActivityTemplate;
  let bestActivityTemplateList = [];
  const activityTemplateValueMap: any = {};
  for (const activityTemplateDTO of activityTemplateDTOList) {
    const { type } = activityTemplateDTO;
    // Dual-read: prefers flat "difficulty", falls back to legacy "<type>Difficulty".
    let currentDifficulty: string =
      readActivityDataField(data, type, "difficulty") || "";
    currentDifficulty = currentDifficulty.toLowerCase();

    const currentDifficultyScore = foundModel[currentDifficulty];
    const splittedTypeList = type.split("_");
    let activityTemplateString = "";
    for (let i = 0; i < splittedTypeList.length; i++) {
      const currentType = splittedTypeList[i].toLowerCase();
      if (i === 0) {
        activityTemplateString += currentType;
      } else {
        activityTemplateString +=
          currentType.charAt(0).toUpperCase() + currentType.substring(1);
      }
    }
    const currentActivityTemplateScore = foundModel[activityTemplateString];
    const currentScore = currentDifficultyScore * currentActivityTemplateScore;
    activityTemplateValueMap[type] = currentScore;
    if (currentScore < bestScore) {
      bestScore = currentScore;
      bestActivityTemplate = type;
      bestActivityTemplateList = [type];
    } else if (currentScore === bestScore) {
      bestActivityTemplateList.push(type);
    }
  }

  return {
    bestScore,
    bestActivityTemplate,
    bestActivityTemplateList,
    activityTemplateValueMap,
  };
};

export const retrieveContestTypeOptionList = () => {
  return [
    {
      value: "QUIZ",
      text: i18n.t("QUIZ"),
    },
  ];
};

export const retrieveFrequencyTypeOptionList = () => {
  return [
    {
      value: "WEEKLY",
      text: i18n.t("WEEKLY"),
    },
  ];
};

export const retrieveDistinctCoterieTypeFromBranchAssignmentDTO =
  (branchAssignmentDTOList: any) => {
    const coterieTypeList = [];
    for (const branchAssignmentDTO of branchAssignmentDTOList) {
      const { coterieType } = branchAssignmentDTO;
      if (
        coterieTypeList.findIndex(
          (foundCoterieType) => foundCoterieType === coterieType
        ) === -1
      ) {
        coterieTypeList.push(coterieType);
      }
    }
    return coterieTypeList;
  };

export const retrieveClockTimeLeft = (
  type: string,
  value: number,
  durationType: string,
  durationInMinutes: number,
  activityProgressDTOSet: any,
  activity: any
) => {
  if (
    type === "BRANCH_ASSIGNMENT" ||
    type === "STANDARD_EXAM" ||
    type === "CONTEST"
  ) {
    if (durationType === "ALL") {
      return durationInMinutes
        ? durationInMinutes * 60 -
            retrieveTotalTimeSpentInSeconds(activityProgressDTOSet)
        : 0;
    } else if (durationType === "EACH") {
      return durationInMinutes
        ? durationInMinutes * 60 -
            retrieveEachTimeSpentInSeconds(activityProgressDTOSet, activity)
        : 0;
    } else return 0;
  } else if (type === "ACTIVITY") {
    return (
      value - retrieveEachTimeSpentInSeconds(activityProgressDTOSet, activity)
    );
  }
  return 0;
};

export const retrieveEachTimeSpentInSeconds = (
  activityProgressList: any,
  activity: any
) => {
  if (!activityProgressList || !activity) return 0;
  const foundActivityProgress = activityProgressList.find(
    (activityProgress: any) =>
      parseFloat(activityProgress.activityId) === parseFloat(activity.id)
  );
  if (foundActivityProgress) {
    return foundActivityProgress.timeSpent / 1000;
  }
  return 0;
};

export const retrieveTotalTimeSpentInSeconds = (activityProgressList: any) => {
  let timeSpent = 0;
  if (!activityProgressList) return 0;
  activityProgressList.forEach((activityProgress: any) => {
    timeSpent += activityProgress.timeSpent;
  });
  return timeSpent / 1000;
};

export const retrieveTotalTimeSpentInMinutes = (activityProgressList: any) => {
  return retrieveTotalTimeSpentInSeconds(activityProgressList) / 60;
};

export const parseContentMapFromData = (data: any) => {
  // The AI activity generator response doesn't include `contentMap` — its outputs are always
  // TEXT-based, so we default to that shape. Activity_data always carries a stringified
  // contentMap; tolerate already-parsed objects too for AI-shape data uniformity.
  const raw = data?.contentMap;
  if (raw == null) return { type: "TEXT" };
  return typeof raw === "string" ? JSON.parse(raw) : raw;
};

/**
 * Reads a raw activity_data field, preferring the flat key (single-template post-migration shape)
 * and falling back to the legacy type-prefixed key. After the cleanup PR drops legacy support,
 * only the flat read path remains.
 */
const ACTIVITY_DATA_PREFIX: Record<string, string> = {
  ORDERING: "ordering",
  DROPDOWN: "dropdown",
  MCSA: "MCSA",
  MCMA: "MCMA",
  MATCHING: "matching",
  GROUPING: "grouping",
  FILL_IN_THE_BLANKS: "fillInTheBlanks",
  OPEN_ENDED: "openEnded",
  TRUE_FALSE: "trueFalse",
};

export const prefixedActivityDataKey = (type: string, flatKey: string): string | null => {
  const prefix = ACTIVITY_DATA_PREFIX[type];
  if (!prefix || !flatKey) return null;
  return prefix + flatKey.charAt(0).toUpperCase() + flatKey.slice(1);
};

export const readActivityDataField = (data: any, type: string, flatKey: string): any => {
  if (data == null) return undefined;
  if (data[flatKey] != null) return data[flatKey];
  const legacyKey = prefixedActivityDataKey(type, flatKey);
  return legacyKey ? data[legacyKey] : undefined;
};

/**
 * Activity_data values are stored as JSON-strings in MongoDB, but the AI activity generator
 * response sends already-parsed objects/arrays under the same field names. Both shapes need
 * to flow through the same preview pipeline, so we accept either: strings get JSON.parse'd,
 * objects pass through as-is.
 */
const parseIfString = (value: any): any =>
  typeof value === "string" ? JSON.parse(value) : value;

export const parseBodyMapFromData = (data: any, type: string) => {
  const raw = readActivityDataField(data, type, "bodyMap");
  return raw != null ? parseIfString(raw) : undefined;
};

/**
 * Parses the JSON string stored in PassageData.data (Mongo) into the bodyMap
 * shape that ActivityBodyContent expects: { "0": "<stringified-entry>", ... }.
 * Returns null when no passage data is present.
 */
export const parsePassageBodyMap = (
  passageData: string | null | undefined,
): any => {
  if (!passageData) return null;
  try {
    const parsed = JSON.parse(passageData);
    if (parsed && parsed.passageBodyMap != null) {
      return parseIfString(parsed.passageBodyMap);
    }
  } catch (e) {
    console.error("Error parsing passage data:", e);
  }
  return null;
};

/**
 * Builds the JSON-stringified body wrapper for PassageData.data.
 * Mirrors constructActivityData's bodyMap shape: { passageBodyMap: "<stringified-entry-map>" }.
 * Each body-map entry is JSON.stringified individually (same as activity body maps).
 */
export const constructPassageData = (bodyMap: any): any => {
  const convertedBodyMap: any = {};
  Object.keys(bodyMap || {}).forEach((key) => {
    convertedBodyMap[key] = JSON.stringify(bodyMap[key]);
  });
  return {
    passageBodyMap: JSON.stringify(convertedBodyMap),
  };
};

export const parseMaterialMapFromData = (data: any, type: string) => {
  if (type === "OPEN_ENDED") return {};
  return parseMaterialMapValue(
    type,
    readActivityDataField(data, type, "materialMap")
  );
};

/**
 * The same parse as `parseMaterialMapFromData`, for a caller that already holds the raw
 * materialMap field (the homework PDF gets it handed over on its own). DROPDOWN, MCSA, MCMA,
 * GROUPING and TRUE_FALSE need their inner values re-parsed; DROPDOWN nests three levels deep.
 */
export const parseMaterialMapValue = (type: string, rawMaterial: any) => {
  if (rawMaterial == null) return undefined;
  // The branches below write the parsed inner values back into the map, so an
  // already-parsed map must not be the caller's own object.
  if (typeof rawMaterial === "object") {
    rawMaterial = JSON.parse(JSON.stringify(rawMaterial));
  }
  if (type === "ORDERING") {
    return parseIfString(rawMaterial);
  } else if (type === "DROPDOWN") {
    const dropdownMaterialMap = parseIfString(rawMaterial);
    Object.keys(dropdownMaterialMap).forEach((materialKey) => {
      dropdownMaterialMap[materialKey] = parseIfString(
        dropdownMaterialMap[materialKey]
      );
      const answerKey = Object.keys(dropdownMaterialMap[materialKey])[0];
      dropdownMaterialMap[materialKey][answerKey] = parseIfString(
        dropdownMaterialMap[materialKey][answerKey]
      );
    });
    return dropdownMaterialMap;
  } else if (type === "MCSA") {
    const MCSAMaterialMap = parseIfString(rawMaterial);
    Object.keys(MCSAMaterialMap).forEach((key) => {
      MCSAMaterialMap[key] = parseIfString(MCSAMaterialMap[key]);
    });
    return MCSAMaterialMap;
  } else if (type === "MCMA") {
    const MCMAMaterialMap = parseIfString(rawMaterial);
    Object.keys(MCMAMaterialMap).forEach((key) => {
      MCMAMaterialMap[key] = parseIfString(MCMAMaterialMap[key]);
    });
    return MCMAMaterialMap;
  } else if (type === "MATCHING") {
    return parseIfString(rawMaterial);
  } else if (type === "GROUPING") {
    const groupingMaterialMap = parseIfString(rawMaterial);
    Object.keys(groupingMaterialMap).forEach((key) => {
      groupingMaterialMap[key] = parseIfString(groupingMaterialMap[key]);
    });
    return groupingMaterialMap;
  } else if (type === "FILL_IN_THE_BLANKS") {
    return parseIfString(rawMaterial);
  } else if (type === "TRUE_FALSE") {
    const trueFalseMaterialMap = parseIfString(rawMaterial);
    Object.keys(trueFalseMaterialMap).forEach((key) => {
      trueFalseMaterialMap[key] = parseIfString(trueFalseMaterialMap[key]);
    });
    return trueFalseMaterialMap;
  }
};

export const convertToBodyMapString = (type: string, bodyMap: any) => {
  const convertedBodyMap: any = {};
  if (type === "ORDERING") {
    Object.keys(bodyMap).forEach(
      (orderingBodyMapKey) =>
        (convertedBodyMap[orderingBodyMapKey] = JSON.stringify(
          bodyMap[orderingBodyMapKey]
        ))
    );
  } else if (type === "DROPDOWN") {
    Object.keys(bodyMap).forEach(
      (dropdownBodyMapKey) =>
        (convertedBodyMap[dropdownBodyMapKey] = JSON.stringify(
          bodyMap[dropdownBodyMapKey]
        ))
    );
  } else if (type === "MCSA") {
    Object.keys(bodyMap).forEach(
      (MCSABodyMapKey) =>
        (convertedBodyMap[MCSABodyMapKey] = JSON.stringify(
          bodyMap[MCSABodyMapKey]
        ))
    );
  } else if (type === "MCMA") {
    Object.keys(bodyMap).forEach(
      (MCMABodyMapKey) =>
        (convertedBodyMap[MCMABodyMapKey] = JSON.stringify(
          bodyMap[MCMABodyMapKey]
        ))
    );
  } else if (type === "MATCHING") {
    Object.keys(bodyMap).forEach(
      (matchingBodyMapKey) =>
        (convertedBodyMap[matchingBodyMapKey] = JSON.stringify(
          bodyMap[matchingBodyMapKey]
        ))
    );
  } else if (type === "GROUPING") {
    Object.keys(bodyMap).forEach(
      (groupingBodyMapKey) =>
        (convertedBodyMap[groupingBodyMapKey] = JSON.stringify(
          bodyMap[groupingBodyMapKey]
        ))
    );
  } else if (type === "FILL_IN_THE_BLANKS") {
    Object.keys(bodyMap).forEach(
      (fillInTheBlanksBodyMapKey) =>
        (convertedBodyMap[fillInTheBlanksBodyMapKey] = JSON.stringify(
          bodyMap[fillInTheBlanksBodyMapKey]
        ))
    );
  } else if (type === "OPEN_ENDED") {
    Object.keys(bodyMap).forEach(
      (openEndedBodyMapKey) =>
        (convertedBodyMap[openEndedBodyMapKey] = JSON.stringify(
          bodyMap[openEndedBodyMapKey]
        ))
    );
  } else if (type === "TRUE_FALSE") {
    Object.keys(bodyMap).forEach(
      (trueFalseBodyMapKey) =>
        (convertedBodyMap[trueFalseBodyMapKey] = JSON.stringify(
          bodyMap[trueFalseBodyMapKey]
        ))
    );
  }
  return convertedBodyMap;
};

export const convertToSolutionMapString = (type: string, solutionMap: any) => {
  const convertedSolutionMap: any = {};
  if (type === "ORDERING") {
    Object.keys(solutionMap).forEach(
      (orderingSolutionMapKey) =>
        (convertedSolutionMap[orderingSolutionMapKey] = JSON.stringify(
          solutionMap[orderingSolutionMapKey]
        ))
    );
  } else if (type === "DROPDOWN") {
    Object.keys(solutionMap).forEach(
      (dropdownSolutionMapKey) =>
        (convertedSolutionMap[dropdownSolutionMapKey] = JSON.stringify(
          solutionMap[dropdownSolutionMapKey]
        ))
    );
  } else if (type === "MCSA") {
    Object.keys(solutionMap).forEach(
      (MCSASolutionMapKey) =>
        (convertedSolutionMap[MCSASolutionMapKey] = JSON.stringify(
          solutionMap[MCSASolutionMapKey]
        ))
    );
  } else if (type === "MCMA") {
    Object.keys(solutionMap).forEach(
      (MCMASolutionMapKey) =>
        (convertedSolutionMap[MCMASolutionMapKey] = JSON.stringify(
          solutionMap[MCMASolutionMapKey]
        ))
    );
  } else if (type === "MATCHING") {
    Object.keys(solutionMap).forEach(
      (matchingSolutionMapKey) =>
        (convertedSolutionMap[matchingSolutionMapKey] = JSON.stringify(
          solutionMap[matchingSolutionMapKey]
        ))
    );
  } else if (type === "GROUPING") {
    Object.keys(solutionMap).forEach(
      (groupingSolutionMapKey) =>
        (convertedSolutionMap[groupingSolutionMapKey] = JSON.stringify(
          solutionMap[groupingSolutionMapKey]
        ))
    );
  } else if (type === "FILL_IN_THE_BLANKS") {
    Object.keys(solutionMap).forEach(
      (fillInTheBlanksSolutionMapKey) =>
        (convertedSolutionMap[fillInTheBlanksSolutionMapKey] = JSON.stringify(
          solutionMap[fillInTheBlanksSolutionMapKey]
        ))
    );
  } else if (type === "OPEN_ENDED") {
    Object.keys(solutionMap).forEach(
      (openEndedSolutionMapKey) =>
        (convertedSolutionMap[openEndedSolutionMapKey] = JSON.stringify(
          solutionMap[openEndedSolutionMapKey]
        ))
    );
  } else if (type === "TRUE_FALSE") {
    Object.keys(solutionMap).forEach(
      (trueFalseSolutionMapKey) =>
        (convertedSolutionMap[trueFalseSolutionMapKey] = JSON.stringify(
          solutionMap[trueFalseSolutionMapKey]
        ))
    );
  }
  return convertedSolutionMap;
};

export const convertToEvaluationRubricMapString = (
  type: string,
  evaluationRubricMap: any
) => {
  const convertedEvaluationRubricMap: any = {};
  if (type === "ORDERING") {
  } else if (type === "DROPDOWN") {
  } else if (type === "MCSA") {
  } else if (type === "MCMA") {
  } else if (type === "MATCHING") {
  } else if (type === "GROUPING") {
  } else if (type === "FILL_IN_THE_BLANKS") {
  } else if (type === "OPEN_ENDED") {
    Object.keys(evaluationRubricMap).forEach(
      (openEndedEvaluationRubricMapKey) =>
        (convertedEvaluationRubricMap[openEndedEvaluationRubricMapKey] =
          JSON.stringify(evaluationRubricMap[openEndedEvaluationRubricMapKey]))
    );
  } else if (type === "TRUE_FALSE") {
  }
  return convertedEvaluationRubricMap;
};

/**
 * Builds the activity_data wire payload for a single activity template.
 *
 * As of the single-template fan-out migration, the output uses FLAT keys
 * ({@code bodyMap}, {@code materialMap}, {@code solutionMap}, {@code difficulty},
 * {@code evaluationRubricMap}) — the legacy type-prefixed keys (MCSABodyMap, etc.)
 * are no longer produced. Existing prefixed docs are read via the dual-read helpers
 * and the migration runner rewrites stored docs to the flat shape.
 *
 * Per-type material massaging is preserved because each template's materialMap has
 * its own JSON-in-JSON nesting structure.
 */
export const constructActivityData = (
  activityTemplateType: string,
  contentMap: any,
  bodyMap: any,
  materialMap: any,
  solutionMap: any,
  difficulty: string,
  evaluationRubricMap: any
) => {
  const constructedData: any = {};
  constructedData["contentMap"] = JSON.stringify(contentMap);
  constructedData["bodyMap"] = JSON.stringify(
    convertToBodyMapString(activityTemplateType, bodyMap)
  );
  constructedData["solutionMap"] = JSON.stringify(
    convertToSolutionMapString(activityTemplateType, solutionMap)
  );
  constructedData["difficulty"] = difficulty;

  if (activityTemplateType === "ORDERING") {
    constructedData["materialMap"] = JSON.stringify(materialMap);
  } else if (activityTemplateType === "DROPDOWN") {
    const converted: any = {};
    Object.keys(materialMap).forEach((key) => {
      const answerKey = Object.keys(materialMap[key])[0];
      const valueList = materialMap[key][answerKey];
      converted[key] = JSON.stringify({
        [answerKey]: JSON.stringify(valueList),
      });
    });
    constructedData["materialMap"] = JSON.stringify(converted);
  } else if (activityTemplateType === "MCSA") {
    const answerKey = Object.keys(materialMap)[0];
    constructedData["materialMap"] = JSON.stringify({
      [answerKey]: JSON.stringify(materialMap[answerKey]),
    });
  } else if (activityTemplateType === "MCMA") {
    const answerKeys = Object.keys(materialMap)[0];
    const splittedAnswerKeyList = answerKeys.split("§");
    constructedData["materialMap"] = JSON.stringify({
      [JSON.stringify(splittedAnswerKeyList)]: JSON.stringify(
        materialMap[answerKeys]
      ),
    });
  } else if (activityTemplateType === "MATCHING") {
    const converted: any = {};
    Object.keys(materialMap).forEach((key) => {
      const subKey = Object.keys(materialMap[key])[0];
      converted[subKey] = materialMap[key][subKey];
    });
    constructedData["materialMap"] = JSON.stringify(converted);
  } else if (activityTemplateType === "GROUPING") {
    const converted: any = {};
    Object.keys(materialMap).forEach((key) => {
      const subKey = Object.keys(materialMap[key])[0];
      converted[subKey] = JSON.stringify(materialMap[key][subKey]);
    });
    constructedData["materialMap"] = JSON.stringify(converted);
  } else if (activityTemplateType === "FILL_IN_THE_BLANKS") {
    const converted: any = {};
    Object.keys(materialMap).forEach((key) => {
      converted[key] = JSON.stringify(materialMap[key]);
    });
    constructedData["materialMap"] = JSON.stringify(converted);
  } else if (activityTemplateType === "OPEN_ENDED") {
    constructedData["materialMap"] = JSON.stringify(materialMap);
    constructedData["evaluationRubricMap"] = JSON.stringify(
      convertToEvaluationRubricMapString("OPEN_ENDED", evaluationRubricMap)
    );
  } else if (activityTemplateType === "TRUE_FALSE") {
    constructedData["materialMap"] = JSON.stringify({
      trueList: JSON.stringify(materialMap.trueList),
      falseList: JSON.stringify(materialMap.falseList),
    });
  }
  return constructedData;
};

export const constructAnswerBasedOnData = (type: string, data: any) => {
  if (!data) return {};
  // Accept either the flat materialMap (post-migration shape) or the legacy type-prefixed key.
  const hasMaterial = readActivityDataField(data, type, "materialMap") != null;
  if (!hasMaterial) return {};
  return constructActivityAnswerMap(type, JSON.parse(JSON.stringify(data)));
};

export const constructActivityItemListBodyOnly = (bodyMap: any) => {
  const itemList: any = [];
  Object.keys(bodyMap).forEach((key) => {
    const bodyItem = JSON.parse(bodyMap[key]);
    if (bodyItem.type === "IMAGE") {
      itemList.push({
        type: "image_url",
        imageUrl: bodyItem.value,
      });
    } else {
      itemList.push({
        type: "text",
        text: bodyItem.value,
      });
    }
  });
  return itemList;
};

export const constructActivityItemListMaterialOnly = (
  materialMap: any,
  type: string
) => {
  const itemList = [];
  if (type === "ORDERING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_order")}: `,
      });
      Object.keys(materialMap).forEach((key) => {
        itemList.push({
          type: "text",
          text: materialMap[key],
        });
      });
    }
  } else if (type === "DROPDOWN") {
    if (Object.keys(materialMap).length > 0) {
      Object.keys(materialMap).forEach((key, index) => {
        const materialInnerMap = JSON.parse(materialMap[key]);
        const innerKey = Object.keys(materialInnerMap)[0];
        const optionList = JSON.parse(materialInnerMap[innerKey]);
        let currentText = "";
        for (const option of optionList) {
          let prefix = "";
          if (option === innerKey) {
            prefix = `[${i18n.t("correct").toUpperCase()}]`;
          } else {
            prefix = `[${i18n.t("incorrect").toUpperCase()}]`;
          }
          currentText += `${prefix}${option}, `;
        }
        itemList.push({
          type: "text",
          text: `${index + 1}. ${currentText.substring(
            0,
            currentText.length - 2
          )}`,
        });
      });
    }
  } else if (type === "MCSA") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answer")}: `,
      });
      Object.keys(materialMap).forEach((key) => {
        itemList.push({
          type: "text",
          text: key,
        });
      });
    }
  } else if (type === "MCMA") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      const splittedTextList = Object.keys(materialMap)[0].split("§");
      splittedTextList.forEach((item) => {
        const parsedItemList = JSON.parse(item);
        for (const parsedItem of parsedItemList) {
          itemList.push({
            type: "text",
            text: parsedItem,
          });
        }
      });
    }
  } else if (type === "MATCHING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        itemList.push({
          type: "text",
          text: `${i18n.t("match")} ${index + 1}:`,
        });
        itemList.push({
          type: "text",
          text: `${key}: ${materialMap[key]}`,
        });
      });
    }
  } else if (type === "GROUPING") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_answers")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        let text = `${i18n.t("group")} ${index + 1}: `;
        itemList.push({
          type: "text",
          text: text,
        });

        const groupName = key;
        let currentText = "";
        JSON.parse(materialMap[groupName]).forEach((item: string) => {
          currentText += `${item}, `;
        });
        currentText = currentText.substring(0, currentText.length - 2);
        itemList.push({
          type: "text",
          text: `${groupName}: [${currentText}]`,
        });
      });
    }
  } else if (type === "FILL_IN_THE_BLANKS") {
    if (Object.keys(materialMap).length > 0) {
      itemList.push({
        type: "text",
        text: `${i18n.t("correct_blanks")}: `,
      });
      Object.keys(materialMap).forEach((key, index) => {
        JSON.parse(materialMap[key]).forEach((item: string) => {
          itemList.push({
            type: "text",
            text: `${index + 1}. ${item}`,
          });
        });
      });
    }
  } else if (type === "OPEN_ENDED") {
  } else if (type === "TRUE_FALSE") {
    if (materialMap.trueList) {
      materialMap.trueList = JSON.parse(materialMap.trueList);
      if (Object.keys(materialMap.trueList).length > 0) {
        itemList.push({
          type: "text",
          text: `${i18n.t("correct_answers")}: `,
        });
        materialMap.trueList.forEach((item: string) => {
          itemList.push({
            type: "text",
            text: item,
          });
        });
      }
    }

    if (materialMap.falseList) {
      materialMap.falseList = JSON.parse(materialMap.falseList);
      if (Object.keys(materialMap.falseList).length > 0) {
        itemList.push({
          type: "text",
          text: `${i18n.t("incorrect_answers")}: `,
        });
        materialMap.falseList.forEach((item: string) => {
          itemList.push({
            type: "text",
            text: item,
          });
        });
      }
    }
  }

  return itemList;
};

export const constructActivityItemListSolutionOnly = (solutionMap: any) => {
  const itemList: any = [];
  Object.keys(solutionMap).forEach((key) => {
    const bodyItem = JSON.parse(solutionMap[key]);
    if (bodyItem.type === "IMAGE") {
      itemList.push({
        type: "image_url",
        imageUrl: bodyItem.value,
      });
    } else {
      itemList.push({
        type: "text",
        text: bodyItem.value,
      });
    }
  });
  return itemList;
};

export const retrieveDifficultyByActivityTypeFromData = (
  type: string,
  data: any
) => {
  const raw = readActivityDataField(data, type, "difficulty");
  return raw != null ? raw : "";
};

export const retrieveOpticalExamTypeOptionList = () => {
  return [
    {
      value: "NO_BOOKLET",
      text: i18n.t("NO_BOOKLET"),
    },
    {
      value: "SINGLE_BOOKLET",
      text: i18n.t("SINGLE_BOOKLET"),
    },
    {
      value: "DOUBLE_BOOKLET",
      text: i18n.t("DOUBLE_BOOKLET"),
    },
    {
      value: "TRIPLE_BOOKLET",
      text: i18n.t("TRIPLE_BOOKLET"),
    },
    {
      value: "QUADRUPLE_BOOKLET",
      text: i18n.t("QUADRUPLE_BOOKLET"),
    },
  ];
};

export const retrieveOpticalExamPartTypeOptionList = () => {
  return [
    {
      value: "READ_ONLY",
      text: i18n.t("READ_ONLY"),
    },
    {
      value: "BOOKLET",
      text: i18n.t("BOOKLET"),
    },
    {
      value: "ANSWER",
      text: i18n.t("ANSWER"),
    },
  ];
};

export const retrieveDelayTypeOptionList = () => {
  return [
    {
      value: "NO_DELAY",
      text: i18n.t("NO_DELAY"),
    },
    {
      value: "ONE_WEEK",
      text: i18n.t("ONE_WEEK"),
    },
    {
      value: "TWO_WEEKS",
      text: i18n.t("TWO_WEEKS"),
    },
  ];
};

export const retrieveActivityCountOptionList = () => {
  return [
    {
      value: 5,
      text: 5,
    },
    {
      value: 10,
      text: 10,
    },
    {
      value: 15,
      text: 15,
    },
    {
      value: 20,
      text: 20,
    },
    {
      value: 25,
      text: 25,
    },
    {
      value: 30,
      text: 30,
    },
  ];
};

export const checkAnswerState = (
  correctAnswer: any,
  learnerAnswer: any
): string => {
  if (Array.isArray(correctAnswer)) {
    return correctAnswer.some((ca: string) => ca === learnerAnswer)
      ? "CORRECT"
      : "INCORRECT";
  }
  return correctAnswer === learnerAnswer ? "CORRECT" : "INCORRECT";
};

/**
 * True when the activity carries a rendered solution (an approved-answer solution
 * map). Mirrors ActivitySolutionContent's own null-guard so callers can gate a
 * "show solution" control without duplicating the parse. Keep the two in sync.
 */
export const checkActivityHasSolution = (
  activityData: any,
  activityTemplateType: string
): boolean => {
  if (!activityTemplateType || !activityData) return false;
  const solutionMapString = readActivityDataField(
    activityData,
    activityTemplateType,
    "solutionMap"
  );
  if (
    !solutionMapString ||
    typeof solutionMapString !== "string" ||
    !(
      solutionMapString.includes("[ONAYLI CEVAP]") ||
      solutionMapString.includes("[APPROVED ANSWER]") ||
      solutionMapString.includes("[GENEHMIGTE ANTWORT]")
    )
  ) {
    return false;
  }
  try {
    const solutionMap = JSON.parse(
      solutionMapString
        .replace(/\n?\[ONAYLI CEVAP\]\n?/g, "")
        .replace(/\n?\[APPROVED ANSWER\]\n?/g, "")
        .replace(/\n?\[GENEHMIGTE ANTWORT\]\n?/g, "")
    );
    return !!solutionMap && Object.keys(solutionMap).length > 0;
  } catch {
    return false;
  }
};

export const checkIsActivityAnswerFullyCorrect = (
  activityTemplateType: string,
  answerMap: any,
  activityData: any
) => {
  if (!answerMap || !activityData) return false;
  if (activityTemplateType === "OPEN_ENDED") return false;

  const materialMap = parseMaterialMapFromData(
    activityData,
    activityTemplateType
  );
  if (!materialMap) return false;

  if (activityTemplateType === "MCSA") {
    const correctAnswer = Object.keys(materialMap)[0];
    return Object.keys(answerMap).every(
      (key) => checkAnswerState(correctAnswer, answerMap[key]) === "CORRECT"
    );
  }

  if (activityTemplateType === "MCMA") {
    const correctAnswerList = JSON.parse(Object.keys(materialMap)[0]);
    return Object.keys(answerMap).every((key) => {
      const learnerAnswers = answerMap[key];
      if (!Array.isArray(learnerAnswers)) return false;
      if (learnerAnswers.length !== correctAnswerList.length) return false;
      return learnerAnswers.every(
        (la: string) => checkAnswerState(correctAnswerList, la) === "CORRECT"
      );
    });
  }

  if (activityTemplateType === "TRUE_FALSE") {
    const correctTrueList = materialMap.trueList || [];
    const correctFalseList = materialMap.falseList || [];
    const learnerTrueList = answerMap.trueList || [];
    const learnerFalseList = answerMap.falseList || [];
    if (learnerTrueList.length !== correctTrueList.length) return false;
    if (learnerFalseList.length !== correctFalseList.length) return false;
    const allTrueCorrect = correctTrueList.every(
      (item: string) => checkAnswerState(correctTrueList, item) === "CORRECT"
        && learnerTrueList.includes(item)
    );
    const allFalseCorrect = correctFalseList.every(
      (item: string) => checkAnswerState(correctFalseList, item) === "CORRECT"
        && learnerFalseList.includes(item)
    );
    return allTrueCorrect && allFalseCorrect;
  }

  if (activityTemplateType === "ORDERING") {
    return Object.keys(answerMap).every(
      (key: string, index: number) =>
        checkAnswerState(index + "", answerMap[key] + "") === "CORRECT"
    );
  }

  if (activityTemplateType === "DROPDOWN") {
    return Object.keys(materialMap).every((key: string) => {
      const correctAnswer = Object.keys(materialMap[key])[0];
      return checkAnswerState(correctAnswer, answerMap[key]) === "CORRECT";
    });
  }

  if (activityTemplateType === "MATCHING") {
    return Object.keys(materialMap).every(
      (key: string) =>
        checkAnswerState(materialMap[key], answerMap[key]) === "CORRECT"
    );
  }

  if (activityTemplateType === "GROUPING") {
    return Object.keys(materialMap).every((key: string) => {
      const correctItems = materialMap[key];
      const learnerItems = answerMap[key] || [];
      if (!Array.isArray(correctItems) || !Array.isArray(learnerItems))
        return false;
      if (correctItems.length !== learnerItems.length) return false;
      return learnerItems.every(
        (item: string) => checkAnswerState(correctItems, item) === "CORRECT"
      );
    });
  }

  if (activityTemplateType === "FILL_IN_THE_BLANKS") {
    return Object.keys(materialMap).every((key: string) => {
      const acceptableAnswers = JSON.parse(materialMap[key]);
      return checkAnswerState(acceptableAnswers, answerMap[key]) === "CORRECT";
    });
  }

  return false;
};
